import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, Shield, UserCheck, UserX, Edit, MoreHorizontal, Users, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


const ROLE_MAP = {
    admin: { label: 'Admin', class: 'sv-badge-gold' },
    approved: { label: 'Aprovado', class: 'sv-badge-success' },
    user: { label: 'Usuario', class: 'sv-badge-muted' },
};

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeMenu, setActiveMenu] = useState(null);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { fetchUsers(); }, []);

    const fetchUsers = async () => {
        try {
            const res = await fetch(`${API}/admin/users`, { headers });
            if (res.ok) setUsers(await res.json());
        } catch {} finally { setLoading(false); }
    };

    const changeRole = async (userId, role) => {
        try {
            const res = await fetch(`${API}/admin/users/${userId}/role`, { method: 'PUT', headers, body: JSON.stringify({ role }) });
            if (res.ok) {
                toast.success(`Role alterado para ${ROLE_MAP[role]?.label || role}`);
                fetchUsers();
            } else toast.error('Erro ao alterar role');
        } catch { toast.error('Erro de conexão'); }
        setActiveMenu(null);
    };

    const filtered = users.filter(u =>
        !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="sv-page" data-testid="users-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Usuarios</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{users.length} usuarios cadastrados</p>
                </div>
                <button className="sv-btn-gold-system sv-btn-system">
                    <Plus size={14} /> Convidar Usuario
                </button>
            </div>

            {/* KPIs */}
            <div className="sv-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="sv-kpi">
                    <span className="sv-kpi-label">Total</span>
                    <span className="sv-kpi-value">{users.length}</span>
                </div>
                <div className="sv-kpi">
                    <span className="sv-kpi-label">Aprovados</span>
                    <span className="sv-kpi-value">{users.filter(u => u.is_approved || u.role === 'admin').length}</span>
                </div>
                <div className="sv-kpi">
                    <span className="sv-kpi-label">Admins</span>
                    <span className="sv-kpi-value">{users.filter(u => u.role === 'admin').length}</span>
                </div>
            </div>

            {/* Search */}
            <div className="sv-card-system">
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-2)' }} />
                    <input type="text" placeholder="Buscar usuario..." value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 rounded-lg text-sm"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                    />
                </div>
            </div>

            {/* Table */}
            <div className="sv-card-system" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div className="sv-empty-state"><Users /><p className="sv-empty-state-title">Carregando...</p></div>
                ) : (
                    <table className="sv-data-table">
                        <thead>
                            <tr><th>Usuario</th><th>Role</th><th>Pontos</th><th>Ofertas</th><th>Criado em</th><th style={{ width: 60 }}></th></tr>
                        </thead>
                        <tbody>
                            {filtered.map(u => (
                                <tr key={u.user_id}>
                                    <td>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold" style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)', border: '1px solid var(--border)' }}>
                                                {u.name?.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="font-medium text-sm" style={{ color: 'var(--text)' }}>{u.name}</p>
                                                <p className="text-xs" style={{ color: 'var(--muted-2)' }}>{u.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className={`sv-badge ${ROLE_MAP[u.role]?.class || 'sv-badge-muted'}`}>{ROLE_MAP[u.role]?.label || u.role}</span></td>
                                    <td style={{ fontWeight: 600 }}>{u.points || 0}</td>
                                    <td>{u.deals_count || 0}</td>
                                    <td style={{ color: 'var(--muted-2)', fontSize: 12 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                                    <td>
                                        <div className="relative">
                                            <button className="sv-btn-system" style={{ padding: '4px 8px' }} onClick={() => setActiveMenu(activeMenu === u.user_id ? null : u.user_id)} data-testid={`user-menu-${u.user_id}`}>
                                                <MoreHorizontal size={12} />
                                            </button>
                                            {activeMenu === u.user_id && (
                                                <div className="absolute right-0 top-full mt-1 z-50 rounded-lg p-1 min-w-[140px]"
                                                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                                                    {['user', 'approved', 'admin'].map(r => (
                                                        <button key={r} className="w-full text-left px-3 py-1.5 rounded text-xs transition-colors flex items-center gap-2"
                                                            style={{ color: u.role === r ? 'var(--sv-gold-primary)' : 'var(--text)', background: u.role === r ? 'rgba(var(--sv-accent-rgb), 0.1)' : 'transparent' }}
                                                            onClick={() => changeRole(u.user_id, r)} data-testid={`user-role-${r}`}>
                                                            <Shield size={10} /> {ROLE_MAP[r]?.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="sv-empty-state"><Users /><p className="sv-empty-state-title">Nenhum usuario encontrado</p></div>
                )}
            </div>
        </div>
    );
}
